import {
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Card,
} from "@mui/material";
import {
  ShoppingCart,
  Add,
  Remove,
  Close,
  Payment,
  CardGiftcard,
} from "@mui/icons-material";
import { useState } from "react";
import GenericModal from "../GenericModal";
import styles from "./BuyHoursButton.module.css";
import { Vocabulary } from "../../Utils/Vocabulary";
import { generatePaymentUrl } from "../../Utils/PaymentUtils";
import { PAYMENT_TYPES } from "../../Utils/Constants";

interface BuyHoursButtonProps {
  order: any;
  onClick?: (orderId: string) => void;
  clientData?: any;
}

export default function BuyHoursButton({
  order,
  onClick,
  clientData,
}: BuyHoursButtonProps) {
  const [openModal, setOpenModal] = useState(false);
  const [hours, setHours] = useState<string>("");
  const supplementaryHoursPrices = order?.product?.supplementaryHoursPrices;
  const supplementaryHourPrice =
    supplementaryHoursPrices?.find(
      (price: any) => price.workPointId === order?.workPointId
    )?.price || 0;

  /**
   * Opens the modal to purchase supplementary hours.
   */
  const handleOpenModal = () => {
    setOpenModal(true);
  };

  /**
   * Closes the modal and resets the hours to an empty string.
   */
  const handleCloseModal = () => {
    setOpenModal(false);
    setHours("");
  };

  /**
   * Confirms the purchase of supplementary hours. If the number of hours is valid,
   * it will call the onClick function with the order ID and close the modal.
   */
  const handleConfirm = () => {
    if (hours && parseInt(hours) > 0) {
      const orderId = order?._id;
      const bonusHours = calculateFreeHours();
      const totalHours = parseInt(hours) + bonusHours;
      const amount = calculateTotal();

      // save supplementary hours data in local storage
      window.localStorage.setItem("supplementaryHours", totalHours.toString());
      window.localStorage.setItem("supplementaryHoursOrderId", orderId);
      window.localStorage.setItem("productName", order?.product?.name || "");
      window.localStorage.setItem("paymentAmount", amount.toString());
      window.localStorage.setItem(
        "paymentType",
        PAYMENT_TYPES.SUPPLEMENTARY_HOURS
      );
      // generate payment url
      generatePaymentUrl({
        clientId: clientData?._id,
        clientData,
        amount,
        orderDescription: `${Vocabulary.buySupplementaryHours} - ${hours} ${Vocabulary.hours}`,
      });

      handleCloseModal();
    }
  };

  /**
   * Calculates the number of free hours from the given hours.
   *
   * The free hours are calculated by dividing the given hours by 5 and rounding down to the nearest integer.
   *
   * @returns {number} The number of free hours.
   */
  const calculateFreeHours = () => {
    const hourCount = parseInt(hours) || 0;
    return Math.floor(hourCount / 5);
  };

  /**
   * Calculates the total price of the supplementary hours.
   *
   * @returns {number} The total price of the supplementary hours.
   */
  const calculateTotal = () => {
    const hourCount = parseInt(hours) || 0;
    return hourCount * supplementaryHourPrice;
  };

  /**
   * Increments the hours by 1 if the current hours is greater than 0.
   */
  const handleIncrement = () => {
    const currentHours = parseInt(hours) || 0;
    setHours((currentHours + 1).toString());
  };

  /**
   * Decrements the hours by 1 if the current hours is greater than 0.
   */
  const handleDecrement = () => {
    const currentHours = parseInt(hours) || 0;
    if (currentHours > 0) {
      setHours((currentHours - 1).toString());
    }
  };

  const modalActions = () => {
    return (
      <div className={styles.modalActions}>
        <Button
          variant="outlined"
          color="error"
          fullWidth
          onClick={handleCloseModal}
          startIcon={<Close />}
        >
          {Vocabulary.cancel}
        </Button>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleConfirm}
          disabled={!hours || parseInt(hours) <= 0}
          startIcon={<Payment />}
        >
          {Vocabulary.pay}
        </Button>
      </div>
    );
  };

  return (
    <>
      <Button
        variant="contained"
        size="small"
        color="primary"
        startIcon={<ShoppingCart />}
        onClick={handleOpenModal}
        className={styles.button}
      >
        {Vocabulary.buySupplementaryHours}
      </Button>

      <GenericModal
        open={openModal}
        onClose={handleCloseModal}
        title={Vocabulary.purchaseSupplementaryHours}
        actions={modalActions()}
        width="500px"
      >
        <Box className={styles.modalContent}>
          {/* Promotional Banner */}
          <Card className={styles.promotionBanner}>
            <Box className={styles.promotionContent}>
              <CardGiftcard className={styles.promotionIcon} />
              <Typography className={styles.promotionText}>
                {Vocabulary.freeHoursPromotion}
              </Typography>
            </Box>
          </Card>

          {/* Service Info Card */}
          <Card className={styles.serviceCard}>
            <Box className={styles.serviceInfo}>
              <Typography className={styles.serviceLabel}>
                {Vocabulary.service}
              </Typography>
              <Typography className={styles.serviceName}>
                {order?.product?.name}
              </Typography>
            </Box>
            <Box className={styles.priceRow}>
              <Typography className={styles.priceLabel}>
                {Vocabulary.pricePerHour}
              </Typography>
              <Typography className={styles.priceValue}>
                {supplementaryHourPrice || 0} RON
              </Typography>
            </Box>
          </Card>

          {/* Hours Selector */}
          <Box className={styles.hoursSelector}>
            <Typography className={styles.selectorLabel}>
              {`${Vocabulary.select} ${Vocabulary.hourCount}`}
            </Typography>
            <Box className={styles.hoursControls}>
              <IconButton
                onClick={handleDecrement}
                disabled={!hours || parseInt(hours) <= 0}
                className={styles.decrementBtn}
              >
                <Remove />
              </IconButton>
              <TextField
                type="number"
                inputProps={{ min: "0", step: "1", max: "999" }}
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                className={styles.hoursInput}
              />
              <IconButton
                onClick={handleIncrement}
                className={styles.incrementBtn}
              >
                <Add />
              </IconButton>
            </Box>
          </Box>

          {/* Bonus Hours Info */}
          {calculateFreeHours() > 0 && (
            <Box className={styles.bonusInfo}>
              <Typography className={styles.bonusLabel}>
                {Vocabulary.bonusHours}:
              </Typography>
              <Typography className={styles.bonusValue}>
                +{calculateFreeHours()}{" "}
                {calculateFreeHours() === 1 ? "oră" : "ore"}
              </Typography>
            </Box>
          )}

          {/* Total Payment */}
          <Card className={styles.totalCard}>
            <Box className={styles.totalContent}>
              <Box>
                <Typography className={styles.totalLabel}>
                  {Vocabulary.totalAmount}
                </Typography>
                <Typography className={styles.totalAmount}>
                  {calculateTotal()} RON
                </Typography>
              </Box>
              {calculateFreeHours() > 0 && (
                <Box className={styles.totalHoursInfo}>
                  <Typography className={styles.hoursBreakdown}>
                    {parseInt(hours)} + {calculateFreeHours()}
                  </Typography>
                  <Typography className={styles.hoursBreakdownLabel}>
                    {Vocabulary.totalWithBonusHours}
                  </Typography>
                </Box>
              )}
            </Box>
          </Card>
        </Box>
      </GenericModal>
    </>
  );
}
